<template>
  <dialog
    ref="dialog"
    :id="id"
    data-test="how-it-works-dialog"
    class="mx-auto w-full md:max-w-xl rounded-lg shadow-lg backdrop:bg-black/40"
  >
    <div class="flex items-start justify-between p-4 pb-2">
      <h1 class="text-xl font-semibold tracking-tight">
        How a Blind Dutch Auction works
      </h1>
      <button
        data-test="close"
        type="button"
        aria-label="Close"
        class="ml-4 -mr-1 -mt-1 px-2 py-1 rounded-lg text-boba-grey-dark hover:bg-boba-sand-light"
        @click="close"
      >
        <i class="fa-solid fa-xmark text-lg" />
      </button>
    </div>

    <ol class="px-4 space-y-3 list-decimal list-outside ml-5 pr-6">
      <li>
        <span class="font-semibold">Enter your maximum bid.</span>
        That's the most you're willing to pay per item, and how many you want.
        Bids are private. Nobody else can see them.
      </li>
      <li>
        <span class="font-semibold">Bids are ranked when the auction closes.</span>
        Items go to the highest bids first, working down the list until every
        item is spoken for.
      </li>
      <li>
        <span class="font-semibold">Everyone pays the same price.</span>
        The lowest winning bid sets the price. Every winner pays that price,
        even if they bid much higher.
      </li>
      <li data-test="limit-rule">
        <span class="font-semibold">{{ limitHeading }}</span>
        {{ limitCopy }}
      </li>
    </ol>

    <div class="m-4 p-4 rounded-lg bg-boba-sand-light space-y-2">
      <p>
        <span class="font-semibold">What this means for you:</span>
        bid the most you'd be happy to pay. You'll never pay more than your
        maximum, and you'll usually pay less.
      </p>
      <p>
        Nothing is charged until the auction closes. You can cancel a bid at any
        time before then. If the last items run out partway through your bid,
        you'll receive fewer than you asked for and only pay for those.
      </p>
    </div>

    <div class="flex justify-end px-4 pb-4">
      <button
        type="button"
        class="px-4 py-2 rounded-lg bg-boba-brown text-white shadow"
        @click="close"
      >
        Got it
      </button>
    </div>
  </dialog>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { Listing } from "@/types";

const props = withDefaults(
  defineProps<{ id?: string; listing?: Listing | null }>(),
  { id: "how-it-works-dialog", listing: null },
);

const perOrder = computed(
  () => props.listing?.buy_limit_scope === "order",
);

const limitHeading = computed(() =>
  perOrder.value
    ? "Purchase limits apply per bid."
    : "Purchase limits count across your bids.",
);

const limitCopy = computed(() => {
  const limit = props.listing?.buy_limit ?? 0;
  if (limit < 1) {
    return "If this auction has a purchase limit, it applies to your total across all of your bids, not to each bid separately.";
  }
  const items = limit === 1 ? "item" : "items";
  if (perOrder.value) {
    return `Each bid can be for up to ${limit} ${items}. You can place more than one bid.`;
  }
  return `You can win up to ${limit} ${items} in total across all of your bids. You can place several bids, but if your winning bids add up to more than ${limit}, the extra units won't clear.`;
});

const dialog = ref<HTMLDialogElement | null>(null);

function open() {
  dialog.value?.showModal?.();
}

function close() {
  dialog.value?.close?.();
}

defineExpose({ open, close });
</script>
